import React, {FC, useMemo, useRef} from 'react';
import s from './AddToCartButton.scss';
import {ButtonContentType, IProduct} from '../../../../types/galleryTypes';
import classNames from 'classnames';
import {
  HoverStyles,
  ButtonPriority,
  StatesButton,
  StatesButtonStates,
  ThreeDotsLoader,
  PaddingModes,
  Tooltip,
  TooltipProps,
} from 'wix-ui-tpa/cssVars';
import {classes as addToCartStyle, st as addToCartStyles} from './AddToCartButton.st.css';
import {useGlobals, withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {useAddToCartState} from './hooks/useAddToCartState';
import {useAddToCartIcon} from './hooks/useAddToCartIcon';
import {IconStatesButton} from './IconStatesButton/IconStatesButton';
import {useAddToCartNotificationEnd} from './hooks/useAddToCartNotificationEnd';
import {useAddToCartText} from './hooks/useAddToCartText/useAddToCartText';
import {AddToCartFlowType} from '../../../../constants';

export enum DataHook {
  AddToCartButton = 'product-item-add-to-cart-button',
  AddToCartLoadingIndicator = 'product-item-add-to-cart-loading-indicator',
  AddToCartIconButtonTooltip = 'product-item-add-to-cart-icon-button-tooltip',
}
export interface IAddToCart extends IGalleryGlobalProps {
  index: number;
  product: IProduct;
  quantity: number;
  showOnHoverClassName: string;
  contentType: ButtonContentType;
}

export const AddToCartComp: FC<IAddToCart> = ({
  product: {id: productId},
  showOnHoverClassName,
  contentType,
  index,
  quantity,
}) => {
  const {
    styles,
    stylesParams,
    experiments,
    textsMap: {selectOptionsButtonText},
    shouldShowMobile,
    handleAddToCart,
  } = useGlobals();

  const {shouldEnableAddToCartColorContrast} = experiments;
  const addToCartButtonRef = useRef<StatesButton>(null);
  const {isAddToCartEnabled, getAddToCartButtonState, hasAddToCartUnselectedOptionError} = useAddToCartState(productId);
  const {handleNotificationEnd} = useAddToCartNotificationEnd(productId);
  const {iconsMapper} = useAddToCartIcon();
  const addToCartButtonText = useAddToCartText(productId, hasAddToCartUnselectedOptionError);

  const handleAddToCartButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const isShowOnlyColorOptionsEnabled = styles.get(stylesParams.gallery_showColorOptionsOnly);
    const isProductOptionsEnabled = styles.get(stylesParams.gallery_showProductOptionsButton);
    const addToCartFlowType = styles.get(stylesParams.gallery_addToCartFlowType);
    const productOptionsDisplayOption = styles.get(stylesParams.gallery_productOptionsDisplayOption);
    const isAddToCartFlowTypeIsQuickAdd = addToCartFlowType === AddToCartFlowType.QUICK_ADD;

    void handleAddToCart({
      productId,
      index,
      quantity,
      isProductOptionsEnabled,
      isShowOnlyColorOptionsEnabled,
      isAddToCartFlowTypeIsQuickAdd,
      productOptionsDisplayOption,
    });
  };

  const tooltipProps: TooltipProps = useMemo(
    () => ({
      'data-hook': DataHook.AddToCartIconButtonTooltip,
      content: selectOptionsButtonText,
      className: s.addToCartIconTooltip,
      disabled: !hasAddToCartUnselectedOptionError,
      fixed: true,
      fluid: true,
      ...(shouldShowMobile
        ? {
            shown: hasAddToCartUnselectedOptionError,
            placement: 'left',
          }
        : {}),
    }),
    [selectOptionsButtonText, hasAddToCartUnselectedOptionError, shouldShowMobile]
  );

  const isButtonEnabled = isAddToCartEnabled();
  const addToCartButtonState = getAddToCartButtonState();
  const shouldShowOnHover = styles.get(stylesParams.gallery_addToCartButtonShowOnHover);

  const addToCartButtonProps = {
    onClick: handleAddToCartButtonClick,
    disabled: !isButtonEnabled,
    className: classNames(
      s.addToCartBtn,
      addToCartStyles(addToCartStyle.addToCartButton, {
        useMobileFont: shouldShowMobile,
        isInStock: isButtonEnabled,
        isDisabled: !isButtonEnabled,
      }),
      {
        [showOnHoverClassName]: shouldShowOnHover && !shouldShowMobile,
        [s.iconButton]: contentType === ButtonContentType.Icon,
        [addToCartStyle.addToCartColorContrastFix]: shouldEnableAddToCartColorContrast,
        [addToCartStyle.addToCartButtonOldDesign]: !shouldEnableAddToCartColorContrast,
      }
    ),
    'data-hook': DataHook.AddToCartButton,
    'aria-label': addToCartButtonText,
    upgrade: contentType === ButtonContentType.IconAndText,
    fullWidth: true,
    hoverStyle:
      /* istanbul ignore next: cant test with jsdom, should be tested by sled */ shouldEnableAddToCartColorContrast
        ? HoverStyles.underline
        : undefined,
    priority:
      /* istanbul ignore next: cant test with jsdom, should be tested by sled */ shouldEnableAddToCartColorContrast
        ? ButtonPriority.primary
        : undefined,
    paddingMode:
      /* istanbul ignore next: cant test with jsdom, should be tested by sled */ shouldEnableAddToCartColorContrast
        ? PaddingModes.explicitPaddings
        : undefined,
  };

  const getIconCompForIconAndTextButton = () => {
    const icon = styles.get(stylesParams.gallery_addToCartButtonIcon);

    const Icon = iconsMapper[icon];
    return <Icon className={s.prefixIcon} />;
  };
  //For Update Sleds
  const stateButtonProps = {
    state: addToCartButtonState,
    prefixIcon:
      addToCartButtonState === StatesButtonStates.IDLE && contentType === ButtonContentType.IconAndText
        ? getIconCompForIconAndTextButton()
        : undefined,
    idleContent: addToCartButtonText,
    onNotificationEnd: handleNotificationEnd,
    ref: addToCartButtonRef,
  };

  return (
    <div className={s.addToCartBtnContainer}>
      {contentType === ButtonContentType.Icon ? (
        <Tooltip {...tooltipProps}>
          <IconStatesButton
            addToCartButtonProps={addToCartButtonProps}
            productId={productId}
            state={addToCartButtonState}
          />
        </Tooltip>
      ) : (
        <StatesButton
          {...addToCartButtonProps}
          {...stateButtonProps}
          wrapContent={true}
          inProgressContent={
            <div className={addToCartStyle.threeDotsButtonWrapper}>
              <span className={s.dummyTextForLoadingWidth}>{addToCartButtonText}</span>
              <ThreeDotsLoader
                className={addToCartStyle.threeDotsButton}
                data-hook={DataHook.AddToCartLoadingIndicator}
              />
            </div>
          }
        />
      )}
    </div>
  );
};

export const AddToCartButton = withGlobals(AddToCartComp);
